<script setup>
import { ref } from 'vue';
import { useShared } from '../state.js';
import { useSync } from '../sync.js';
import { useAuth } from '../auth.js';
import { useSetups } from '../setups.js';
import { useToasts } from '../use_toasts.js';
import { logDescriptionIfChanged } from '../setup_log.js';

const { inputs } = useShared();
const { syncAll } = useSync();
const { isLoggedIn } = useAuth();
const { activeSetupId } = useSetups();
const { pushToast } = useToasts();

const saving = ref(false);

// Same call/feedback shape as account_widget.vue's "Sync now" — a
// deliberate click always reports its own outcome, success or failure.
// Description doesn't auto-sync or auto-log as you type (see sync.js's
// scheduleAutoSync and setup_log.js's own header comment) — Save is the
// only thing that pushes it and logs the change.
async function onSave() {
  saving.value = true;
  try {
    const [ok] = await Promise.all([
      syncAll({ silent: false }),
      logDescriptionIfChanged(activeSetupId.value).catch((err) => {
        console.warn('Failed to log description change:', err.message);
      }),
    ]);
    if (ok) pushToast('Saved.', { variant: 'success' });
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="centered">
    <textarea
      class="description-input"
      placeholder="Describe this setup — bow, arrows, tuning notes, whatever's useful to remember later."
      v-model="inputs.description"
    ></textarea>

    <button v-if="isLoggedIn" type="button" class="btn-copy" @click="onSave" :disabled="saving">
      {{ saving ? 'Saving…' : 'Save' }}
    </button>
  </div>
</template>

<style scoped>

.description-input {
  width: 100ch;
  max-width: 100%;
  min-height: 6em;
  padding: var(--space-sm);
  font: inherit;
  font-size: var(--font-size-sm);
  color: var(--color-text);
  background: var(--color-bg);
  border: var(--border);
  border-radius: var(--border-radius);
  resize: vertical;
  box-sizing: border-box;
}

</style>
